import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import SubTitle from "../../../components/common/SubTitle";
import { CampaignButtonVariant } from "../../../components/common/Button/Button";
import CampaignCard from "../../../components/common/CampaignCard/CampaignCard";
import CampaignCardSkeleton from "../../../components/common/CampaignCard/CampaignCardSkeleton";
import { Box, Typography, useTheme } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { Images } from "../../../utils/Images";
import { getCampaigns, getUrgentLifeCampaigns } from "../../../api/services/campaignService";
import { canViewPrivateCampaign } from "../../../utils/authUtils";
import { matchesCategory } from "../../../utils/helper";
import { fetchCmsHomeAction } from "../../../store/slices/cmsSlice";
import LifeCardV2 from "../LifeCard/LifeCardV2";
import LifeCardSkeletonV2 from "../LifeCard/LifeCardSkeletonV2";

const UrgentNeedsCampaign = ({ campaignData = null }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isAbove1200 = useMediaQuery("(min-width: 1200px)");
  const isAbove1440 = useMediaQuery("(min-width: 1440px)");
  const isDesktop = useMediaQuery("(min-width: 1390px)");
  const isTablet = useMediaQuery("(min-width: 600px) and (max-width: 1389px)");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const cmsHome = useSelector((state) => state.cms.home);
  const [campaigns, setCampaigns] = useState([]);
  const [sectionLimit, setSectionLimit] = useState(3);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isLifeDetailRoute = useMemo(() => {
    const p = (location.pathname || "").toLowerCase();
    return p.startsWith("/life/");
  }, [location.pathname]);

  useEffect(() => {
    if (!cmsHome) {
      dispatch(fetchCmsHomeAction());
    }
  }, [cmsHome, dispatch]);

  const currentId = campaignData?.id;

  useEffect(() => {
    let isMounted = true;
    const privacyFilter = (list) =>
      list.filter((c) => {
        if (c.IsPrivate === 0 || !c.IsPrivate) return true;
        if (c.IsPrivate === 1) return canViewPrivateCampaign(user, isAuthenticated);
        return true;
      });

    const excludeCurrent = (list) =>
      currentId == null ? list : list.filter((c) => c.id !== currentId);

    const fetchCampaigns = async () => {
      try {
        setLoading(true);
        if (isLifeDetailRoute) {
          const response = await getUrgentLifeCampaigns();
          if (!isMounted) return;
          const data = response?.data || [];
          setCampaigns(currentId ? data.filter((c) => c.id !== currentId) : data);
          setSectionLimit(response?.limit ?? 3);
        } else {
          const response = await getCampaigns();
          if (!isMounted) return;
          const allCampaigns = response?.data || [];
          const privacyFiltered = privacyFilter(allCampaigns);
          const urgentCampaigns = privacyFiltered.filter((campaign) =>
            matchesCategory(campaign.category, "Urgent")
          );
          const sorted = [...urgentCampaigns].sort((a, b) => {
            const dateA = new Date(a.updatedAt || a.createdAt || 0);
            const dateB = new Date(b.updatedAt || b.createdAt || 0);
            return dateB - dateA;
          });
          setCampaigns(excludeCurrent(sorted).slice(0, 3));
        }
        if (isMounted) setError(null);
      } catch (err) {
        if (isMounted) {
          console.error("Error fetching urgent / life campaigns:", err);
          setCampaigns([]);
          setError("Unable to load campaigns right now.");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchCampaigns();
    return () => {
      isMounted = false;
    };
  }, [user, isAuthenticated, isLifeDetailRoute, currentId]);

  const visibleCampaigns = useMemo(() => {
    if (isAbove1440) {
      return campaigns.slice(0, 3);
    }
    return campaigns;
  }, [campaigns, isAbove1440]);

  const descriptionText = useMemo(() => {
    const sections = Array.isArray(cmsHome?.data) ? cmsHome.data : [];
    const path = (location.pathname || "").toLowerCase();

    let targetId = 104; // Normal Campaign by default
    if (path.startsWith("/story/")) {
      targetId = 105; // Separate description for story
    } else if (path.startsWith("/life")) {
      targetId = 106; // Life Campaign description
    }

    const section = sections.find((s) => s?.id === targetId);
    return (
      section?.description ||
      ""
    );
  }, [cmsHome, location.pathname]);
  return (
    <div
      style={{
        width: "100%",
        // minHeight: "855px", // Changed from fixed height to minHeight
        borderRadius: isMobile ? "10px" : "36px",
        backgroundColor: "rgba(232, 232, 232, 0.36)",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-evenly",
        alignItems: "center",
        marginTop: isMobile ? "0px" : "40px",
        marginBottom: isMobile ? "0px" : "40px",
        padding: isMobile ? "26px 0px 20px 0px" : "60px 0px 40px 0px", // Responsive padding - removed left/right padding
        position: "relative", // Added for absolute positioning of decorative elements
        overflow: "hidden", // Added to contain the decorative elements
      }}
    >
      {/* Decorative Orange Sparks - Top Left (Desktop Only) */}
      <img
        src={Images.orangeSparks}
        alt="Orange Sparks"
        style={{
          position: "absolute",
          top: isMobile ? "10px" : isAbove1200 ? "61px" : "20px",
          left: isMobile ? "10px" : isAbove1200 ? "116px" : "20px",
          width: isMobile ? "40px" : isAbove1200 ? "50px" : "45px",
          height: "auto",
          zIndex: 1,
          display: isMobile ? "none" : "block", // Hide on mobile
        }}
      />

      {/* Decorative Images.heart - Top Right (Desktop Only) */}
      <img
        src={Images.heart}
        alt="Images.heart"
        style={{
          position: "absolute",
          top: isMobile ? "35px" : isAbove1200 ? "116px" : "80px",
          right: isMobile ? "35px" : isAbove1200 ? "275px" : "30px",
          width: isMobile ? "16px" : isAbove1200 ? "22px" : "25px",
          height: "auto",
          zIndex: 1,
          display: "block",
        }}
      />

      {/* Decorative String Hand - Mobile Only */}
      <img
        src={Images.stringHand}
        alt="String Hand"
        style={{
          position: "absolute",
          bottom: "16px",
          left: "0px",
          width: "53px",
          height: "auto",
          zIndex: 1,
          display: isMobile ? "block" : "none", // Show only on mobile
          transform: "scaleX(-1)", // Invert horizontally
        }}
      />

      {/* Title Box */}
      {/* <div
        style={{
          marginBottom: isMobile ? "8px" : "16px", // Responsive margin bottom
          position: "relative",
          zIndex: 2,
        }}
      >
        <Title isMobile={isMobile}>Featured Campaigns</Title>
      </div> */}

      {/* Subtitle Box */}
      {/* <div
        style={{
          width: "100%",
          maxWidth: isMobile ? "280px" : "506px", // Responsive max width
          marginBottom: isMobile ? "20px" : "38px", // Responsive margin bottom
          position: "relative",
          zIndex: 2,
        }}
      >
        <SubTitle isMobile={isMobile}>
          Urgent Campaigns That Need Your Help Now
        </SubTitle>
      </div> */}

      {/* Title */}
      <Box
        sx={{ mb: isMobile ? "8px" : "16px", zIndex: 2, position: "relative" }}
      >
        <SubTitle isMobile={isMobile}>Urgent Needs</SubTitle>
      </Box>

      {/* Subtitle */}
      <Box
        sx={{
          width: "100%",
          maxWidth: isMobile ? "280px" : "581px",
          mb: isMobile ? "20px" : "38px",
          fontSize: "14px",
        }}
      >
        <Typography
          isMobile={isMobile}
          sx={{
            fontSize: isMobile ? "12px" : "14px",
            color: "#666666",
            textAlign: "center",
            fontWeight: 400,
            paddingInline: "10px",
          }}
        >
          {descriptionText}
        </Typography>
      </Box>

      {/* Campaign Cards Section */}
      <div
        style={{
          display: isDesktop ? "grid" : "flex",
          gridTemplateColumns: isDesktop ? "repeat(3, 1fr)" : "none",
          gridTemplateRows: isDesktop ? "repeat(2, auto)" : "none",
          width: "100%",
          justifyContent: isDesktop ? "center" : "space-evenly",
          alignItems: "center",
          gap: isMobile ? "16px" : isTablet ? "20px" : "24px",
          marginBottom: isMobile ? "20px" : "48px",
          paddingLeft: isMobile ? "16px" : "34px",
          paddingRight: isMobile ? "16px" : "34px",
          overflowX: isDesktop ? "hidden" : "auto",
          overflowY: "hidden",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
        className="hide-scrollbar"
      >
        {loading ? (
          Array.from({ length: sectionLimit }).map((_, index) =>
            isLifeDetailRoute ? (
              <LifeCardSkeletonV2 key={`life-skel-${index}`} />
            ) : (
              <CampaignCardSkeleton key={`skeleton-${index}`} />
            )
          )
        ) : error ? (
          // Show error message if fetch failed
          <Typography
            sx={{
              color: "#666666",
              textAlign: "center",
              fontSize: "14px",
              padding: "20px",
            }}
          >
            {error}
          </Typography>
        ) : visibleCampaigns.length > 0 ? (
          visibleCampaigns.map((campaign) =>
            isLifeDetailRoute ? (
              <LifeCardV2 key={campaign.id} lifeData={campaign} />
            ) : (
              <CampaignCard key={campaign.id} campaignData={campaign} />
            )
          )
        ) : (
          <Typography
            sx={{
              color: "#666666",
              textAlign: "center",
              fontSize: "14px",
              padding: "20px",
            }}
          >
            {isLifeDetailRoute
              ? "No other life campaigns available at the moment."
              : "No urgent campaigns available at the moment."}
          </Typography>
        )}
      </div>

      {/* Button Box */}
      <div>
        <CampaignButtonVariant
          text="See All Campaigns"
          isMobile={isMobile}
          onClick={() =>
            isLifeDetailRoute ? navigate("/life") : navigate("/campaigns")
          }
        />
      </div>
    </div>
  );
};

export default UrgentNeedsCampaign;
